/** Проигрыш: таймер, реванш, совет — чистая модель без DOM. */
import { type BalanceData, type HeroId, type PurchaseRequest } from "@newfungame/contracts";
import { type EconomyState, applyPurchase, canPurchase, heroPower } from "@newfungame/engine";

export interface UpgradeAdvice {
  kind: "heroLevel" | "heroStar";
  heroId: HeroId;
  /** Уровень (или число звёзд) до и после покупок. */
  from: number;
  to: number;
  /** Сколько монет уйдёт на все покупки. */
  cost: number;
  /** Прибавка силы героя (урон в секунду), целые проценты. */
  damagePct: number;
  /** Покупки по порядку — для API (`PurchaseRequest`). */
  purchases: PurchaseRequest[];
}

/** Сколько уровней совет покупает разом: заметная прибавка, но не весь кошелёк в одного героя. */
export const UPGRADE_MAX_STEPS = 10;

interface Candidate {
  req: Extract<PurchaseRequest, { kind: "heroLevel" | "heroStar" }>;
  cost: number;
  gain: number;
}

function candidates(b: BalanceData, s: EconomyState): Candidate[] {
  const out: Candidate[] = [];
  for (const p of s.heroes) {
    if (!p.unlocked) continue;
    const before = heroPower(b, p.heroId, p.level, p.stars);
    for (const kind of ["heroLevel", "heroStar"] as const) {
      const req = { kind, heroId: p.heroId };
      const check = canPurchase(b, s, req);
      if (!check.ok) continue;
      const after =
        kind === "heroLevel"
          ? heroPower(b, p.heroId, p.level + 1, p.stars)
          : heroPower(b, p.heroId, p.level, p.stars + 1);
      out.push({ req, cost: check.cost, gain: after - before });
    }
  }
  return out;
}

/**
 * Самая выгодная покупка после проигрыша — по экономике engine (`canPurchase`, цены, `heroPower`):
 *
 * 1. Кандидаты — уровень и звезда каждого открытого героя, на которые хватает монет прямо сейчас.
 * 2. Выгода — прибавка суммарной силы (урон в секунду, `heroPower`) на монету; лучшая — наибольшая
 *    `gain / cost`, при равенстве — дешевле, затем — раньше открытый герой. Tech не советуем: проигрыш — это
 *    «не успели убить», и прямой ответ на него — урон героев.
 * 3. Выбранную покупку повторяем, пока хватает монет и она остаётся доступной (уровни — до
 *    {@link UPGRADE_MAX_STEPS} за раз; звезда — одна).
 *
 * `null` — купить нечего (не хватает ни на что): окно показывает только «В бой».
 */
export function upgradeAdvice(b: BalanceData, state: EconomyState): UpgradeAdvice | null {
  const list = candidates(b, state);
  if (list.length === 0) return null;
  let best = list[0]!;
  for (const c of list.slice(1)) {
    const lhs = c.gain * best.cost;
    const rhs = best.gain * c.cost;
    if (lhs > rhs || (lhs === rhs && c.cost < best.cost)) best = c;
  }
  const hero = state.heroes.find((h) => h.heroId === best.req.heroId)!;
  const steps = best.req.kind === "heroStar" ? 1 : UPGRADE_MAX_STEPS;
  let cur: EconomyState = state;
  const purchases: PurchaseRequest[] = [];
  let cost = 0;
  while (purchases.length < steps) {
    const r = applyPurchase(b, cur, best.req);
    if (!r.ok) break;
    cur = r.state;
    cost += r.spent;
    purchases.push(best.req);
  }
  const after = cur.heroes.find((h) => h.heroId === hero.heroId)!;
  const p0 = heroPower(b, hero.heroId, hero.level, hero.stars);
  const p1 = heroPower(b, after.heroId, after.level, after.stars);
  const level = best.req.kind === "heroLevel";
  return {
    kind: best.req.kind,
    heroId: hero.heroId,
    from: level ? hero.level : hero.stars,
    to: level ? after.level : after.stars,
    cost,
    damagePct: p0 > 0 ? Math.round(((p1 - p0) * 100) / p0) : 0,
    purchases,
  };
}
